import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import PaymentMethods from "elements/register/cartMenu/PaymentMethods";
import AccountsModal from "components/accountsModal/AccountsModal";
import CreditBookModal from "components/creditBookModal/CreditBookModal";
import { LocaleCurrency } from "helpers/Locale";
import { capitalize, errorMessage } from "helpers/Helpers";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { IOrder } from "redux/reducers/models/order";
import { IPrizeAccount } from "pages/secure/facility/settings/prizeAccounts/PrizeAccount";
import {
    IExchangeCalculateResponse,
    IExchangePaymentTransaction,
    IExchangeRefundTransaction,
} from "api/rpc/2024-04/facilityAdmin/order/exchange";
import GiftCardRedeem from "components/giftCardRedeem/GiftCardRedeem";
import CheckoutForm, { cancelManualCreditPaymentIntent } from "components/checkoutForm/CheckoutForm";
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements } from "@stripe/react-stripe-js";
import { showError, showSuccess } from "redux/actions/ui";
import { StripeCardCvcElementChangeEvent, StripeCardExpiryElementChangeEvent, StripeCardNumberElementChangeEvent } from "@stripe/stripe-js";
import { PostTransaction } from "api/rpc/2024-04/facilityAdmin/order/transaction";
import { StatusCode } from "api/protocols";
import { GetGiftCard } from "api/rpc/2024-04/facilityAdmin/payment/giftCard";
import { GetAccount } from "api/rpc/2024-04/facilityAdmin/client/accounts";
import { GetCreditBook } from "api/rpc/2024-04/facilityAdmin/client/creditBook";

interface IProps {
    open: boolean;
    summary: IExchangeCalculateResponse | null;
    order?: IOrder;
    submitting?: boolean;
    onCancel: () => void;
    onConfirmRefund: (refundTransactions: IExchangeRefundTransaction[]) => void;
    onConfirmCollect: (paymentTransaction: IExchangePaymentTransaction) => void;
}

export default function ExchangeCheckoutSheet(props: IProps) {
    const { open, summary, order, submitting, onCancel, onConfirmRefund, onConfirmCollect } = props;
    const { t } = useTranslation();
    const { getCurrencySymbol, displayCurrency } = useCurrencyFacility();
    const elements = useElements();
    const dispatch = useAppDispatch();
    const { facilityStore, authStore, terminalStore } = useAppSelector(store => store);

    const [refundTenders, setRefundTenders] = useState<IExchangeRefundTransaction[]>([]);
    const [paymentOption, setPaymentOption] = useState<Record<string, any> | null>(null);
    const [giftCardVisible, setGiftCardVisible] = useState(false);
    const [accountVisible, setAccountVisible] = useState(false);
    const [creditBookVisible, setCreditBookVisible] = useState(false);

    // Stripe Payment Related Functions and Variables
    const [manualStripeState, setManualStripeState] = useState({
        clientSecret: "",
        visible: false,
        transaction: undefined,
        elementComplete: {
            cardNumber: false,
            cardExpiry: false,
            cardCvc: false,
        },
    });
    const [submitTrigger, setSubmitTrigger] = useState<boolean>(false);
    const [manualProcessing, setManualProcessing] = useState<boolean>(false);

    const elementsComplete =
        manualStripeState.elementComplete.cardNumber === true &&
        manualStripeState.elementComplete.cardExpiry === true &&
        manualStripeState.elementComplete.cardCvc === true;

    const collectAmount = Number(summary?.amount_due ?? 0);
    const refundDue = Math.abs(Number(summary?.net_balance ?? 0));
    const settlementType = summary?.settlement_type;

    const collectPaymentOptions: Array<Record<string, any>> = useMemo(() => {
        return ((facilityStore?.paymentOptions as Array<Record<string, any>>) ?? []).filter(option => {
            if (!option?.active && option?.active !== undefined) {
                return false;
            }
            if (option?.type === "deposit") {
                return false;
            }
            return true;
        });
    }, [facilityStore?.paymentOptions]);

    useEffect(() => {
        if (!open || !summary) {
            return;
        }

        setGiftCardVisible(false);
        setAccountVisible(false);
        setCreditBookVisible(false);

        if (summary.settlement_type === "refund") {
            setRefundTenders(
                (summary.refund_transactions ?? [])
                    .filter(txn => Number(txn.amount) > 0 && txn.parent_id)
                    .map(txn => ({
                        parent_id: txn.parent_id,
                        amount: Number(txn.amount),
                    })),
            );
        }

        if (summary.settlement_type === "collect") {
            const cashOption = collectPaymentOptions.find(option => option.payment_method === "cash");
            setPaymentOption(cashOption ?? collectPaymentOptions[0] ?? null);
        }
    }, [open, summary?.settlement_type, summary?.net_balance, collectPaymentOptions]);

    const refundTotal = roundCurrency(refundTenders.reduce((sum, txn) => sum + Number(txn.amount || 0), 0));
    const refundMatchesDue = Math.abs(refundTotal - refundDue) < 0.01;
    const cardReaderAvailable = Boolean(terminalStore?.reader);
    const isCardCollect =
        paymentOption?.payment_method === "card" || paymentOption?.processing_type === "terminal";

    function parentTransaction(parentId: number) {
        return order?.transactions?.find(transaction => transaction.id === parentId);
    }

    function tenderLabel(parentId: number) {
        const parent = parentTransaction(parentId);
        return parent?.payment_type
            ? capitalize(parent.payment_type)
            : parent?.payment_method
                ? capitalize(parent.payment_method)
                : t("Payment");
    }

    function handleRefundAmountChange(parentId: number, value: string, maximumRefundable: number) {
        const nextAmount = Number(value);
        if (Number.isNaN(nextAmount) || nextAmount < 0 || nextAmount > Number(maximumRefundable)) {
            return;
        }

        setRefundTenders(prev => {
            const existing = prev.find(txn => txn.parent_id === parentId);
            if (!existing) {
                return [...prev, { parent_id: parentId, amount: nextAmount }];
            }
            return prev.map(txn => (txn.parent_id === parentId ? { ...txn, amount: nextAmount } : txn));
        });
    }

    async function submitCollect(extras: Partial<IExchangePaymentTransaction> = {}) {
        if (!paymentOption || collectAmount <= 0) {
            return;
        }

        // Temporary checks for requiring gift card, account and credit books to cover the whole payment amount
        if (paymentOption && extras?.gift_card_code) {
            const res = await GetGiftCard({ code: extras?.gift_card_code }, true);
            if (res?.status !== StatusCode.OK || res?.data?.length === 0) {
                dispatch(showError(errorMessage(res, t("Error getting gift card details"))));
                return;
            } else {
                const giftCard = res?.data[0];
                if (giftCard?.balance < collectAmount) {
                    dispatch(showError(t("Gift card balance does not cover required payment amount")));
                    return;
                }
            }
        } else if (paymentOption && extras?.account_id) {
            const res = await GetAccount({ id: extras?.account_id }, true);
            if (res?.status !== StatusCode.OK || res?.data?.length === 0) {
                dispatch(showError(errorMessage(res, t("Error getting account details"))));
                return;
            } else {
                const account = res?.data[0];
                if ((account?.balance + collectAmount) > account?.limit) {
                    dispatch(showError(t("Payment exceeds account limit")));
                    return;
                }
            }
        } else if (paymentOption && extras?.credit_book_id) {
            const res = await GetCreditBook({ id: extras?.credit_book_id }, true);
            if (res?.status !== StatusCode.OK || res?.data?.length === 0) {
                dispatch(showError(errorMessage(res, t("Error getting credit book details"))));
                return;
            } else {
                const creditBook = res?.data[0];
                if ((creditBook?.balance - collectAmount) < 0) {
                    dispatch(showError(t("Credit book has insufficient funds")));
                    return;
                }
            }
        }

        onConfirmCollect({
            amount: collectAmount,
            payment_method: paymentOption.payment_method,
            payment_option_id: paymentOption.id,
            ...extras,
        });
    }

    async function handleCollectOk() {
        if (paymentOption?.payment_method === "gift_card") {
            setGiftCardVisible(true);
            return;
        }
        if (paymentOption?.payment_method === "account") {
            setAccountVisible(true);
            return;
        }
        if (paymentOption?.payment_method === "credit_book") {
            setCreditBookVisible(true);
            return;
        }

        if (paymentOption?.payment_method === "manual_card") {
            const transactionRes = await PostTransaction(
                {
                    kind: "sale",
                    amount: collectAmount,
                    order_id: order.id,
                    payment_option_id: paymentOption?.id,
                    exchange_collect: true,
                },
                true,
            );

            if (transactionRes.status === StatusCode.OK) {
                setManualStripeState(prevState => ({
                    ...prevState,
                    transaction: transactionRes.data,
                    clientSecret: transactionRes.data.client_secret,
                    visible: true,
                }));
                return;
            } else {
                dispatch(showError(t("secure.facility.register.register_new.cart_menu.006")));
                return;
            }
        }
        void submitCollect();
    }

    if (!open || !summary || settlementType === "even") {
        return null;
    }

    function handleManualProcessing() {
        if (manualProcessing === true) {
            setManualProcessing(false);
        } else {
            setManualProcessing(true);
        }
    }

    function handleCardSectionChange(
        e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
    ) {
        setManualStripeState(prevState => ({
            ...prevState,
            elementComplete: { ...prevState.elementComplete, [e.elementType]: e.complete },
        }));
    }

    return (
        <>
            {settlementType === "refund" ? (
                <Sheet
                    open={open}
                    size="small"
                    height="flexible"
                    closable
                    stacked
                    title={t("Issue exchange refund")}
                    onCancel={onCancel}
                    onOk={() => onConfirmRefund(refundTenders.filter(txn => Number(txn.amount) > 0))}
                    cancelText={t("Back")}
                    okText={t("Issue refund")}
                    okDisabled={!refundMatchesDue || submitting}
                    okLoading={submitting}
                    backDropCancel={false}
                >
                    <p className="text-lg mb-4">
                        {t("Refund {{amount}} using the original tenders.", {
                            amount: displayCurrency(refundDue),
                        })}
                    </p>
                    <FormLayout>
                        {(summary.refund_transactions ?? [])
                            .filter(txn => txn.parent_id)
                            .map(txn => {
                                const current = refundTenders.find(tender => tender.parent_id === txn.parent_id);
                                return (
                                    <FormLayout.Group key={txn.parent_id}>
                                        <Input
                                            type="number"
                                            label={tenderLabel(txn.parent_id)}
                                            prefix={getCurrencySymbol(summary.currency) ?? "$"}
                                            suffix={`/ ${String(txn.maximum_refundable)}`}
                                            value={String(current?.amount ?? 0)}
                                            onChange={e =>
                                                handleRefundAmountChange(
                                                    txn.parent_id,
                                                    e.target.value,
                                                    Number(txn.maximum_refundable),
                                                )
                                            }
                                        />
                                    </FormLayout.Group>
                                );
                            })}
                    </FormLayout>
                    <div className="flex items-center justify-between mt-4">
                        <p className="font-semibold">{t("Refund total")}</p>
                        <LocaleCurrency currency={summary.currency} amount={refundTotal} />
                    </div>
                    {!refundMatchesDue ? (
                        <p className="mt-2">{t("Refund tenders must equal {{amount}}", { amount: displayCurrency(refundDue) })}</p>
                    ) : null}
                </Sheet>
            ) : (
                <Sheet
                    open={open}
                    size="small"
                    height="flexible"
                    closable
                    stacked
                    title={t("Collect exchange payment")}
                    onCancel={onCancel}
                    onOk={handleCollectOk}
                    cancelText={t("Back")}
                    okText={
                        paymentOption?.payment_method === "gift_card"
                            ? t("Enter Gift Card")
                            : paymentOption?.payment_method === "account"
                                ? t("Select account")
                                : paymentOption?.payment_method === "credit_book"
                                    ? t("Select credit book")
                                    : isCardCollect
                                        ? t("Charge card")
                                        : t("Collect payment")
                    }
                    okDisabled={!paymentOption || collectAmount <= 0 || submitting || (isCardCollect && !cardReaderAvailable)}
                    okLoading={submitting}
                    backDropCancel={false}
                >
                    <p className="text-lg mb-4">
                        {t("Amount due {{amount}}", { amount: displayCurrency(collectAmount) })}
                    </p>
                    <PaymentMethods
                        onChange={option => setPaymentOption(option !== undefined ? option : null)}
                        paymentOptions={collectPaymentOptions}
                        cardReaderAvailable={cardReaderAvailable}
                        cart={order}
                        customer={order?.customer}
                        paymentAmount={collectAmount}
                        value={paymentOption}
                    />
                </Sheet>
            )}

            <GiftCardRedeem
                redeemGiftCardsActive={giftCardVisible}
                customerId={order?.customer_id}
                onCancel={() => {
                    setGiftCardVisible(false);
                }}
                onOk={info => submitCollect({ gift_card_code: info?.giftCardCode })}
            />

            <AccountsModal
                loadAccounts={accountVisible}
                onCancel={() => setAccountVisible(false)}
                onOk={account => {
                    setAccountVisible(false);
                    void submitCollect({ account_id: account?.id });
                }}
                customerId={order?.customer_id ?? order?.customer?.id ?? 0}
                searchAccountPermission={!!authStore?.user?.permissions?.accounts_charge_all}
            />

            <CreditBookModal
                loadCreditBooks={creditBookVisible}
                onCancel={() => setCreditBookVisible(false)}
                onOk={(selectedCreditBook: IPrizeAccount) => {
                    setCreditBookVisible(false);
                    void submitCollect({ credit_book_id: selectedCreditBook?.id });
                }}
                customerId={order?.customer_id ?? order?.customer?.id ?? 0}
            />

            {/* Manual Credit Card Sheet*/}
            <Sheet
                size={"small"}
                closable
                title={t("secure.facility.register.register_new.cart_menu.040")}
                cancelText={t("secure.facility.register.register_new.cart_menu.081")}
                open={manualStripeState.visible}
                okText={t("secure.facility.register.register_new.cart_menu.041")}
                okDisabled={!elementsComplete}
                cancelDisabled={manualProcessing}
                backDropCancel={false}
                stacked
                onOk={() => {
                    if (!submitTrigger) {
                        setSubmitTrigger(!submitTrigger);
                    }
                }}
                onCancel={async () => {
                    await cancelManualCreditPaymentIntent(
                        manualStripeState?.transaction?.payment_intent_id as string,
                        manualStripeState?.transaction?.id as number,
                    );
                    if (elements) {
                        elements.getElement(CardNumberElement).clear();
                        elements.getElement(CardExpiryElement).clear();
                        elements.getElement(CardCvcElement).clear();
                    }
                    setManualStripeState(prevState => ({
                        ...prevState,
                        visible: false,
                        transaction: {},
                    }));
                }}
            >
                <CheckoutForm
                    exchangeCollect
                    onChange={handleCardSectionChange}
                    onSubmit={() => handleManualProcessing()}
                    trigger={submitTrigger}
                    transaction={manualStripeState.transaction}
                    onError={(message: string) => {
                        setSubmitTrigger(false);
                        setManualProcessing(false);
                        dispatch(showError(message));
                    }}
                    onSuccess={captureTransaction => {
                        // Clean values
                        dispatch(showSuccess(t("secure.facility.register.register_new.cart_menu.042")));
                        setManualStripeState(prevState => ({
                            ...prevState,
                            visible: false,
                            transaction: {},
                            elementComplete: {
                                cardCvc: false,
                                cardExpiry: false,
                                cardNumber: false,
                            },
                        }));
                        setSubmitTrigger(false);
                        if (!captureTransaction?.id) {
                            dispatch(showError(t("Unable to complete exchange")));
                            return;
                        }
                        void submitCollect({ transaction_id: captureTransaction.id });
                    }}
                />
            </Sheet>
        </>
    );
}

function roundCurrency(value: number) {
    return Math.round(value * 100) / 100;
}
